import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik, useField } from 'formik';
import {
  AtsSettingsDocument,
  useSetAtsProgressionEnabledMutation,
  useSetAtsAllowedRejectionReasonIdsMutation,
  useSetAtsRejectOnBehalfOfUserRemoteIdMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  Button,
  EditFormWrapper,
  FieldLabel,
  FormikRadioButtonGroup,
  SelectFormField,
  Table,
  Tag,
  TagColor,
  TagSize,
  TextInput,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import type { TSelectOption } from '@spotted-zebra-uk/ui-components';
import {
  AtsSettings,
  allowedReasonsSummary,
  WORKABLE_TOOL,
} from './IntegrationsTab.helpers';
import styles from './IntegrationsTab.module.scss';

const dataTestId = 'integrations-rejection-form';

/**
 * Outbound-only: the inbound ATS→SZ rejection sync runs unconditionally,
 * so the copy names that asymmetry (verified against the monolith
 * write-back consumers, EM-135).
 */
const WRITE_BACK_CAPTION =
  'When enabled, rejecting a candidate in Spotted Zebra also rejects them ' +
  'in the ATS, and a rejection reason becomes required for candidates on ' +
  'roles linked to an ATS requisition. Rejections made in the ATS always ' +
  'sync into Spotted Zebra regardless of this setting.';

const ALLOWED_REASONS_CAPTION =
  'Reasons recruiters can pick in the reject modal, including its ' +
  '“Recently used” group. Selecting none shows all of them. This filters ' +
  'the picker only — it does not validate what is sent to the ATS.';

const ON_BEHALF_OF_CAPTION =
  'The Workable user remote ID (not an email address or name) rejections ' +
  'are attributed to. Only sent for Workable integrations. A wrong ID makes ' +
  'every outbound rejection for this company fail until it is corrected or ' +
  'cleared. Leave blank to disable.';

type ReasonOption = { label: string; value: string };

/**
 * Resolve saved ids to display names, alphabetically. An id with no match is
 * a reason deleted on the ATS since it was saved — surface it by id rather
 * than dropping it, so it can still be seen and removed.
 */
function toSelectedReasons(
  ids: readonly string[] | null | undefined,
  options: ReasonOption[]
): ReasonOption[] {
  const byId = new Map(options.map(option => [option.value, option.label]));
  return (ids ?? [])
    .map(id => ({ value: id, label: byId.get(id) ?? id }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

type RejectionCardProps = {
  companyId: number;
  atsIdentifier: string | null | undefined;
  atsSettings: AtsSettings;
  reasonOptions: ReasonOption[];
  reasonsLoading: boolean;
};

export function RejectionCard(props: RejectionCardProps) {
  const [isEditing, setIsEditing] = useState(false);

  return isEditing ? (
    <RejectionForm
      {...props}
      onCancelClick={() => setIsEditing(false)}
      onSave={() => setIsEditing(false)}
    />
  ) : (
    <RejectionView
      atsIdentifier={props.atsIdentifier}
      atsSettings={props.atsSettings}
      reasonOptions={props.reasonOptions}
      onEditClick={() => setIsEditing(true)}
    />
  );
}

function RejectionView({
  atsIdentifier,
  atsSettings,
  reasonOptions,
  onEditClick,
}: {
  atsIdentifier: string | null | undefined;
  atsSettings: AtsSettings;
  reasonOptions: ReasonOption[];
  onEditClick: () => void;
}) {
  const isWorkable = atsIdentifier === WORKABLE_TOOL;
  const selected = toSelectedReasons(
    atsSettings.allowedRejectionReasonIds,
    reasonOptions
  );

  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Rejection</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        <InfoItem
          label="Rejection write-back"
          value={atsSettings.progressionEnabled ? 'Enabled' : 'Disabled'}
        />
        <InfoItem
          label="Allowed rejection reasons"
          value={
            // Names need the catalogue; fall back to the count while it
            // loads (or when the ATS returned nothing).
            selected.length > 0 && reasonOptions.length > 0 ? (
              <div className={styles.reasonTags}>
                {selected.map(reason => (
                  <Tag
                    key={reason.value}
                    tagColor={TagColor.BLUE}
                    size={TagSize.SMALL}
                    isDismissible={false}
                    isPresentational
                    ariaLabel={reason.label}
                  >
                    {reason.label}
                  </Tag>
                ))}
              </div>
            ) : (
              allowedReasonsSummary(atsSettings.allowedRejectionReasonIds)
            )
          }
        />
        {isWorkable && (
          <InfoItem
            label="Reject on behalf of (Workable user ID)"
            value={atsSettings.rejectOnBehalfOfUserRemoteId ?? 'Not set'}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Selected reasons as rows, the rest behind a searchable dropdown — the
 * catalogue can run to hundreds of reasons, so only the chosen ones are
 * rendered and long names stay on one full-width line.
 */
function ReasonSelector({
  options,
  selectedIds,
  onChange,
}: {
  options: ReasonOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const selectedSet = new Set(selectedIds);
  const available = options.filter(option => !selectedSet.has(option.value));
  const selected = toSelectedReasons(selectedIds, options);

  return (
    <>
      <SelectFormField
        id={`${dataTestId}__add-reason`}
        label="Add a reason"
        placeholder={
          available.length > 0
            ? 'Search reasons…'
            : 'All reasons already selected'
        }
        isSearchable
        isDisabled={available.length === 0}
        maxMenuHeight={280}
        options={available}
        onChange={(option: TSelectOption) => {
          if (option?.value) {
            onChange([...selectedIds, option.value]);
          }
        }}
      />
      <div className={styles.reasonListHeader}>
        <span className={styles.reasonCount}>
          {selectedIds.length === 0
            ? `No selection — all ${options.length} reasons allowed`
            : `${selectedIds.length} of ${options.length} selected`}
        </span>
        {selectedIds.length > 0 && (
          <Button
            variant="secondary"
            size="small"
            onClick={() => onChange([])}
            data-testid={`${dataTestId}__clear-reasons`}
          >
            Clear selection
          </Button>
        )}
      </div>
      {selected.length > 0 ? (
        <Table
          className={styles.reasonTable}
          hasPagination={false}
          isSortable={false}
          headerData={[
            { key: 'name', header: 'Allowed reason' },
            { key: 'remove', header: '' },
          ]}
          rowData={selected.map(reason => ({
            id: reason.value,
            name: reason.label,
            remove: (
              <Button
                variant="secondary"
                size="small"
                className={styles.reasonRemoveButton}
                ariaLabel={`Remove ${reason.label}`}
                onClick={() =>
                  onChange(selectedIds.filter(id => id !== reason.value))
                }
                data-testid={`${dataTestId}__remove-${reason.value}`}
              >
                Remove
              </Button>
            ),
          }))}
        />
      ) : (
        <span className={styles.fieldIdCaption}>
          No reasons selected — recruiters get all {options.length} reasons.
        </span>
      )}
    </>
  );
}

/**
 * Workable-only: the user this company's outbound rejections are
 * attributed to (sent as remote_fields.workable.on_behalf_of_user_remote_id).
 * Always-visible label, same rationale as ResultsDeliveryCard's local
 * labelled input.
 */
function OnBehalfOfField({ name, label }: { name: string; label: string }) {
  const [field, , helpers] = useField<string>(name);
  return (
    <FieldLabel id={name} label={label}>
      <TextInput
        id={name}
        value={field.value}
        ariaLabel={label}
        onChange={value => helpers.setValue(value)}
        onBlur={() => helpers.setTouched(true)}
        placeholder="Workable user remote ID — blank to disable"
      />
    </FieldLabel>
  );
}

type RejectionFormValues = {
  progressionEnabled: string;
  allowedRejectionReasonIds: string[];
  rejectOnBehalfOfUserRemoteId: string;
};

type RejectionFormProps = RejectionCardProps & {
  onCancelClick: () => void;
  onSave: () => void;
};

function RejectionForm({
  companyId,
  atsIdentifier,
  atsSettings,
  reasonOptions,
  reasonsLoading,
  onCancelClick,
  onSave,
}: RejectionFormProps) {
  const { handleMsgType } = useNotification();
  const isWorkable = atsIdentifier === WORKABLE_TOOL;
  const [setAtsProgressionEnabledMutation] =
    useSetAtsProgressionEnabledMutation();
  const [setAtsAllowedRejectionReasonIdsMutation] =
    useSetAtsAllowedRejectionReasonIdsMutation();
  const [setAtsRejectOnBehalfOfUserRemoteIdMutation] =
    useSetAtsRejectOnBehalfOfUserRemoteIdMutation();

  const initialProgressionEnabled = atsSettings.progressionEnabled
    ? EnableDisableValue.Enabled
    : EnableDisableValue.Disabled;
  const initialAllowedReasonIds = atsSettings.allowedRejectionReasonIds ?? [];

  const settingsRefetch = {
    refetchQueries: [{ query: AtsSettingsDocument, variables: { companyId } }],
  };

  const handleSave = async (values: RejectionFormValues) => {
    const progressionChanged =
      values.progressionEnabled !== initialProgressionEnabled;

    // Set semantics: reordering the selection is not a change.
    const allowedReasonsChanged =
      JSON.stringify([...values.allowedRejectionReasonIds].sort()) !==
      JSON.stringify([...initialAllowedReasonIds].sort());

    const trimmedOnBehalfOf = values.rejectOnBehalfOfUserRemoteId.trim();
    const onBehalfOfChanged =
      isWorkable &&
      trimmedOnBehalfOf !== (atsSettings.rejectOnBehalfOfUserRemoteId ?? '');

    try {
      await Promise.all([
        progressionChanged
          ? setAtsProgressionEnabledMutation({
              variables: {
                companyId,
                enabled:
                  values.progressionEnabled === EnableDisableValue.Enabled,
              },
              ...settingsRefetch,
            })
          : Promise.resolve(),
        allowedReasonsChanged
          ? setAtsAllowedRejectionReasonIdsMutation({
              variables: {
                companyId,
                // Empty selection clears the filter (stored as null).
                ids:
                  values.allowedRejectionReasonIds.length > 0
                    ? values.allowedRejectionReasonIds
                    : null,
              },
              ...settingsRefetch,
            })
          : Promise.resolve(),
        onBehalfOfChanged
          ? setAtsRejectOnBehalfOfUserRemoteIdMutation({
              variables: {
                companyId,
                // Blank clears the value (stored as null, field no longer sent).
                userRemoteId: trimmedOnBehalfOf || null,
              },
              ...settingsRefetch,
            })
          : Promise.resolve(),
      ]);
      handleMsgType({
        type: TNotification.success,
        title: 'Rejection settings updated.',
        dismissable: true,
      });
      onSave();
    } catch {
      handleMsgType({
        type: TNotification.error,
        title: 'Rejection settings could not be updated',
        message: 'Something went wrong. Please try again.',
        dismissable: true,
      });
    }
  };

  const initialValues: RejectionFormValues = {
    progressionEnabled: initialProgressionEnabled,
    allowedRejectionReasonIds: [...initialAllowedReasonIds],
    rejectOnBehalfOfUserRemoteId:
      atsSettings.rejectOnBehalfOfUserRemoteId ?? '',
  };

  return (
    <Formik<RejectionFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm, values, setFieldValue }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Rejection"
          formId={dataTestId}
          hasResetAllButton={false}
          data-testid={dataTestId}
        >
          <form
            onSubmit={e => e.preventDefault()}
            id={`${dataTestId}-element`}
            className={styles.form}
          >
            <FormikRadioButtonGroup
              id="progressionEnabled"
              name="progressionEnabled"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label="Rejection write-back"
              data-testid={`${dataTestId}__progression-radio-group`}
              useFormikField={useField}
              className={styles.radioGroup}
            />
            <span className={styles.caption}>{WRITE_BACK_CAPTION}</span>
            {isWorkable && (
              <>
                <OnBehalfOfField
                  name="rejectOnBehalfOfUserRemoteId"
                  label="Reject on behalf of (Workable user ID)"
                />
                <span className={styles.fieldIdCaption}>
                  {ON_BEHALF_OF_CAPTION}
                </span>
              </>
            )}
            {!reasonsLoading && (
              <div className={styles.resultCard}>
                <h4 className={styles.resultCardTitle}>Rejection reasons</h4>
                {reasonOptions.length > 0 ? (
                  <>
                    <span className={styles.fieldIdCaption}>
                      {ALLOWED_REASONS_CAPTION}
                    </span>
                    <ReasonSelector
                      options={reasonOptions}
                      selectedIds={values.allowedRejectionReasonIds}
                      onChange={ids =>
                        setFieldValue('allowedRejectionReasonIds', ids)
                      }
                    />
                  </>
                ) : (
                  <span className={styles.fieldIdCaption}>
                    No rejection reasons are available from this company&apos;s
                    ATS.
                  </span>
                )}
              </div>
            )}
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
}
